import { useId, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Button, Dialog, Field, Icon, IconButton, Input, TextArea } from "@agentsaas/ui";
import type { Agent, AgentId, Message, Part } from "@agentsaas/protocol";
import { useT } from "../app/i18n";
import { useAssets } from "../data/assets";
import { activeMention, applyMention, isRef, mentionCandidates, parseMentions, refLabel, textOfParts } from "./model";

export interface ComposerSend {
  parts: Part[];
  mentions: AgentId[];
  replyTo?: string;
}

type RefPart = Extract<Part, { type: "ref" }>;

/**
 * 聊天室输入框：@ 补全（↑↓ 选择，Enter / Tab 确认，Esc 关闭）、引用回复、附件引用（资产库 / 手填引用地址）。
 * Enter 发送，Shift+Enter 换行；输入法组合中不发送。
 */
export function RoomComposer({
  pid,
  members,
  replyTo,
  replyName,
  onCancelReply,
  disabled,
  disabledHint,
  onSend,
}: {
  pid: string;
  members: Agent[];
  replyTo: Message | null;
  replyName: string;
  onCancelReply(): void;
  disabled?: boolean;
  disabledHint?: string;
  onSend(v: ComposerSend): Promise<boolean>;
}) {
  const { t } = useT();
  const listId = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [caret, setCaret] = useState(0);
  const [refs, setRefs] = useState<RefPart[]>([]);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState<number | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [busy, setBusy] = useState(false);

  const mention = activeMention(text, caret);
  const candidates = useMemo(() => (mention ? mentionCandidates(members, mention.query) : []), [mention, members]);
  const open = !!mention && candidates.length > 0 && dismissed !== mention.start;
  const mentions = parseMentions(text, members);

  // 选中候选后，在文本更新后的同一帧把光标放到插入的名字后面
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (pendingCaret.current === null || !el) return;
    el.focus();
    el.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  }, [text]);

  const pick = (a: Agent) => {
    const next = applyMention(text, caret, a.name);
    pendingCaret.current = next.caret;
    setText(next.text);
    setCaret(next.caret);
    setActive(0);
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (disabled || busy) return;
    const body = text.trim();
    if (!body && !refs.length) return;
    const parts: Part[] = [...(body ? [{ type: "text" as const, text: body }] : []), ...refs];
    setBusy(true);
    const ok = await onSend({ parts, mentions, replyTo: replyTo?.id });
    setBusy(false);
    if (ok) {
      setText("");
      setCaret(0);
      setRefs([]);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const d = e.key === "ArrowDown" ? 1 : -1;
        setActive((i) => (i + d + candidates.length) % candidates.length);
        return;
      }
      if ((e.key === "Enter" && !e.shiftKey) || e.key === "Tab") {
        e.preventDefault();
        pick(candidates[Math.min(active, candidates.length - 1)]!);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setDismissed(mention!.start);
        return;
      }
    }
    if (e.key === "Escape" && replyTo) {
      e.preventDefault();
      onCancelReply();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };

  const optionId = (i: number) => `${listId}-opt-${i}`;
  const showNoMention = !!text.trim() && mentions.length === 0 && members.length > 0;

  return (
    <>
    <form className="composer room-composer" onSubmit={(e) => void submit(e)} data-testid="room-composer">
      {replyTo ? (
        <div className="room-composer__reply" data-testid="reply-bar">
          <Icon name="reply" size={16} />
          <span className="grow ellipsis small">
            <strong>{t("room.replying_to", { name: replyName })}</strong> <span className="muted">{textOfParts(replyTo.parts)}</span>
          </span>
          <IconButton icon="close" label={t("room.cancel_reply")} onClick={onCancelReply} data-testid="reply-cancel" />
        </div>
      ) : null}
      {refs.length ? (
        <div className="composer__refs">
          {refs.map((r) => (
            <span key={r.ref} className="chip" data-testid="composer-ref">
              <Icon name={r.ref.startsWith("asset://") ? "image" : "paperclip"} size={14} />
              <span className="ellipsis">{refLabel(r)}</span>
              <button type="button" className="chip__x" aria-label={t("chat.remove_ref", { name: refLabel(r) })} onClick={() => setRefs((xs) => xs.filter((x) => x.ref !== r.ref))}>
                <Icon name="close" size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {disabled && disabledHint ? <div className="small muted">{disabledHint}</div> : null}
      <div className="composer__row room-composer__row">
        <IconButton icon="paperclip" label={t("room.attach")} onClick={() => setAttaching(true)} disabled={disabled} data-testid="room-attach" />
        <div className="room-composer__field">
          {open ? (
            <ul className="mention-pop" role="listbox" id={listId} aria-label={t("room.mention_list")} data-testid="mention-list">
              {candidates.map((a, i) => (
                <li
                  key={a.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === active}
                  className={`mention-pop__item${i === active ? " is-active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(a);
                  }}
                  data-testid="mention-option"
                >
                  <Icon name="user" size={16} />
                  <strong>{a.name}</strong>
                  <span className="muted small">{a.role}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <TextArea
            ref={ref}
            className="composer__input"
            rows={1}
            value={text}
            disabled={disabled}
            placeholder={t("room.placeholder")}
            aria-label={t("room.placeholder")}
            aria-describedby={`${listId}-hint`}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-activedescendant={open ? optionId(active) : undefined}
            enterKeyHint="send"
            onChange={(e) => {
              setText(e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
              setActive(0);
            }}
            onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
            onKeyDown={onKeyDown}
            data-testid="room-input"
          />
        </div>
        <IconButton type="submit" icon="send" variant="primary" label={t("chat.send")} disabled={disabled || busy || (!text.trim() && !refs.length)} data-testid="room-send" />
      </div>
      <div id={`${listId}-hint`} className="small muted composer__hint">
        {showNoMention ? t("room.no_mention_hint") : t("room.send_hint")}
      </div>
    </form>
      {/* 放在 form 外：React 的合成事件会穿过 portal 冒泡，内层表单提交会触发外层 onSubmit */}
      <AttachDialog
        pid={pid}
        open={attaching}
        onClose={() => setAttaching(false)}
        onAdd={(r) => {
          setRefs((xs) => (xs.some((x) => x.ref === r.ref) ? xs : [...xs, r]));
          setAttaching(false);
        }}
      />
    </>
  );
}

/** 附件引用：从资产库挑，或手填引用地址 */
function AttachDialog({ pid, open, onClose, onAdd }: { pid: string; open: boolean; onClose(): void; onAdd(r: RefPart): void }) {
  const { t } = useT();
  const assets = useAssets(pid, {}, { enabled: open });
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | undefined>();
  const add = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isRef(value)) return setError(t("room.attach.invalid"));
    onAdd({ type: "ref", ref: value.trim() });
    setValue("");
    setError(undefined);
  };
  return (
    <Dialog open={open} onClose={onClose} title={t("room.attach.title")} closeLabel={t("shell.close")}>
      <div className="stack">
        <div>
          <div className="ui-field__label">{t("room.attach.assets")}</div>
          {assets.items.length ? (
            <ul className="list attach-assets">
              {assets.items.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    className="list-item"
                    onClick={() => onAdd({ type: "ref", ref: `asset://${a.id}@${a.latestVersion}`, name: a.name })}
                    data-testid="attach-asset"
                  >
                    <Icon name={a.kind === "model3d" ? "cube" : "image"} />
                    <span className="grow ellipsis">{a.name}</span>
                    <span className="muted small">{t("asset.version_short", { version: a.latestVersion })}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">{t("room.attach.none_assets")}</p>
          )}
        </div>
        <form className="stack" onSubmit={add}>
          <Field label={t("room.attach.ref")} hint={t("room.attach.ref_hint")} error={error}>
            {(id, describedBy) => (
              <Input id={id} aria-describedby={describedBy} value={value} onChange={(e) => setValue(e.target.value)} className="mono" data-testid="attach-ref" autoCapitalize="off" autoCorrect="off" />
            )}
          </Field>
          <Button type="submit" variant="secondary" disabled={!value.trim()} data-testid="attach-ref-add">
            {t("chat.attach.add")}
          </Button>
        </form>
      </div>
    </Dialog>
  );
}
